import { StatusBar } from 'expo-status-bar';
import { StyleSheet, Text, View, Dimensions, Animated, Easing, TouchableOpacity, Platform } from 'react-native';
import { useEffect, useRef, useState, useMemo } from 'react';
import { LinearGradient } from 'expo-linear-gradient';

// GradientText component that works on both iOS and Android
const GradientText = ({ 
  children, 
  colors = ['#9d4edd', '#5a189a', '#6711b7'] as const, 
  style, 
  ...props 
}: {
  children: React.ReactNode;
  colors?: readonly [string, string, ...string[]];
  style?: any;
}) => {
  if (Platform.OS === 'android') {
    return (
      <View style={[style, { overflow: 'hidden' }]}>
        <LinearGradient
          colors={colors}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={[StyleSheet.absoluteFill]}
        />
        <Text {...props} style={[style, { backgroundColor: 'transparent', opacity: 0 }]}>
          {children}
        </Text>
        <Text {...props} style={[style, { position: 'absolute', color: 'transparent' }]}>
          {children}
        </Text>
      </View>
    );
  }

  // iOS
  return (
    <Text 
      {...props} 
      style={[
        style, 
        {
          backgroundImage: `linear-gradient(90deg, ${colors.join(', ')})`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }
      ]}
    >
      {children}
    </Text>
  );
};

const { width, height } = Dimensions.get('window');

// Particle component for background
const Particle = ({ 
  size, 
  delay, 
  duration = 15, 
  startX, 
  startY, 
  endY, 
  opacity = 0.8 
}: { 
  size: number; 
  delay: number; 
  duration?: number; 
  startX: number; 
  startY: number; 
  endY?: number; 
  opacity?: number 
}) => {
  const animation = useRef(new Animated.Value(0)).current;
  const position = useRef({
    x: Math.random() * width,
    y: Math.random() * height,
  }).current;
  
  const translateY = animation.interpolate({
    inputRange: [0, 1],
    outputRange: [0, endY !== undefined ? endY - startY : -100],
  });

  const translateX = animation.interpolate({
    inputRange: [0, 0.5, 1],
    outputRange: [0, (Math.random() - 0.5) * 50, 0],
  });

  const particleStyle = useMemo(() => ({
    position: 'absolute' as const,
    left: startX,
    top: startY,
    width: size,
    height: size,
    backgroundColor: `rgba(${157 + Math.random() * 50}, ${73 + Math.random() * 50}, 221, ${opacity})`,
    borderRadius: 100,
    opacity: animation.interpolate({
      inputRange: [0, 0.3, 0.7, 1],
      outputRange: [0, opacity * 0.8, opacity * 0.9, 0],
    }),
    transform: [
      { translateY },
      { translateX },
    ],
  }), [size, position.x, position.y]);

  useEffect(() => {
    const animateParticle = () => {
      const anim = Animated.sequence([
        Animated.timing(animation, {
          toValue: 1,
          duration: duration * 1000,
          useNativeDriver: true,
          easing: Easing.linear,
        }),
        Animated.timing(animation, {
          toValue: 0,
          duration: 0,
          useNativeDriver: true,
        })
      ]);
      
      anim.start(() => animateParticle());
      return anim;
    };

    const timeout = setTimeout(() => {
      animateParticle();
    }, delay * 1000);

    return () => {
      clearTimeout(timeout);
      animation.stopAnimation();
    };
  }, [animation, delay, duration]);

  return <Animated.View style={particleStyle} />;
};

// Create particles for the background
const Particles = () => {
  const particles = Array(50).fill(0).map((_, i) => ({
    id: i,
    size: Math.random() * 3 + 1,
    delay: Math.random() * 20,
    duration: Math.random() * 15 + 10,
    startX: Math.random() * width,
    startY: Math.random() * height,
    endY: -50 - Math.random() * 50,
    opacity: 0.3 + Math.random() * 0.7
  }));

  return (
    <>
      {particles.map((particle) => (
        <Particle
          key={particle.id}
          size={particle.size}
          delay={particle.delay}
          duration={particle.duration}
          startX={particle.startX}
          startY={particle.startY}
          endY={particle.endY}
          opacity={particle.opacity}
        />
      ))}
    </>
  );
};

export default function App() {
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideUp = useRef(new Animated.Value(50)).current;
  const pulseAnim = useRef(new Animated.Value(1)).current;
  const [isHovered, setIsHovered] = useState(false);

  // Main animations
  useEffect(() => {
    const pulseAnimation = Animated.loop(
      Animated.sequence([
        Animated.timing(pulseAnim, {
          toValue: 1.05,
          duration: 3000,
          useNativeDriver: true,
          easing: Easing.inOut(Easing.ease),
        }),
        Animated.timing(pulseAnim, {
          toValue: 0.95,
          duration: 3000,
          useNativeDriver: true,
          easing: Easing.inOut(Easing.ease),
        }),
      ])
    );

    const mainAnimation = Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 2000,
        useNativeDriver: true,
      }),
      Animated.timing(slideUp, {
        toValue: 0,
        duration: 1200,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      })
    ]);

    mainAnimation.start();
    pulseAnimation.start();

    return () => {
      mainAnimation.stop();
      pulseAnimation.stop();
    };
  }, [fadeAnim, slideUp, pulseAnim]);

  // Hover effect for social icons
  const handleHover = (isHovering: boolean) => {
    Animated.spring(pulseAnim, {
      toValue: isHovering ? 1.2 : 1,
      useNativeDriver: true,
    }).start();
    setIsHovered(isHovering);
  };

  // Generate random positions for floating elements
  const floatingElements = Array(15).fill(0).map((_, i) => ({
    id: i,
    size: Math.random() * 15 + 5,
    left: Math.random() * width,
    top: Math.random() * height,
    duration: Math.random() * 15000 + 15000,
    delay: Math.random() * 10000,
    opacity: 0.1 + Math.random() * 0.4,
  }));

  return (
    <LinearGradient
      colors={['#000000', '#000000']}
      style={styles.container}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
    >
      <StatusBar style="light" />
      
      {/* Animated Background Elements */}
      <View style={styles.background}>
        <Particles />
        {floatingElements.map((el) => {
          const rotateAnim = useRef(new Animated.Value(0)).current;
          
          useEffect(() => {
            const animate = () => {
              Animated.loop(
                Animated.timing(rotateAnim, {
                  toValue: 1,
                  duration: 60000 + Math.random() * 30000,
                  useNativeDriver: true,
                  easing: Easing.linear,
                })
              ).start();
            };
            
            const timeout = setTimeout(animate, el.delay);
            return () => clearTimeout(timeout);
          }, []);
          
          const rotation = rotateAnim.interpolate({
            inputRange: [0, 1],
            outputRange: ['0deg', '360deg']
          });
          
          return (
            <Animated.View
              key={el.id}
              style={[
                styles.floatingElement,
                {
                  width: el.size,
                  height: el.size,
                  left: el.left,
                  top: el.top,
                  opacity: el.opacity,
                  transform: [{ rotate: rotation }],
                },
              ]}
            >
              <View style={[
                styles.innerGlow, 
                { 
                  width: el.size, 
                  height: el.size,
                  backgroundColor: `rgba(${Math.floor(157 + Math.random() * 50)}, ${Math.floor(73 + Math.random() * 50)}, 221, ${0.3 + Math.random() * 0.3})`
                }
              ]} />
            </Animated.View>
          );
        })}
        
        <Animated.View style={[styles.circle, styles.circle1, {
          transform: [
            { scale: pulseAnim.interpolate({ inputRange: [0.9, 1.1], outputRange: [0.9, 1.1] }) }
          ]
        }]} />
        <Animated.View style={[styles.circle, styles.circle2, {
          transform: [
            { scale: pulseAnim.interpolate({ inputRange: [0.9, 1.1], outputRange: [1.1, 0.9] }) }
          ]
        }]} />
        <Animated.View style={[styles.circle, styles.circle3, {
          transform: [
            { 
              rotate: pulseAnim.interpolate({
                inputRange: [0, 1],
                outputRange: ['0deg', '360deg']
              })
            }
          ]
        }]} />
      </View>

      <Animated.View 
        style={[
          styles.content,
          {
            opacity: fadeAnim,
            transform: [
              { translateY: slideUp },
            ]
          }
        ]}
      >
        <View style={styles.titleContainer}>
          <Animated.View 
            style={[
              {
                transform: [{ scale: pulseAnim }],
              }
            ]}
          >
            <GradientText style={styles.title}>
              Conferra
            </GradientText>
          </Animated.View>
          
          <Text style={styles.comingSoon}>COMING SOON</Text>
          <Text style={styles.version}>V1.0</Text>
        </View>

       
      
      </Animated.View>
    </LinearGradient>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0a0a12',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  floatingElement: {
    position: 'absolute',
    borderRadius: 100,
    justifyContent: 'center',
    alignItems: 'center',
    opacity: 0,
  },
  floatingElementGlow: {
    position: 'absolute',
    borderRadius: 100,
    shadowColor: '#9d4edd',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.8,
    shadowRadius: 10,
    elevation: 5,
  },
  innerGlow: {
    backgroundColor: 'rgba(115, 91, 137, 0.5)',
    borderRadius: 100,
    position: 'absolute',
  },
  background: {
    position: 'absolute',
    width: '100%',
    height: '100%',
  },
  circle: {
    position: 'absolute',
    borderRadius: 1000,
    backgroundColor: 'rgba(138, 43, 226, 0.1)',
  },
  circle1: {
    width: Math.max(width, height) * 1.5,
    height: Math.max(width, height) * 1.5,
    top: -height * 0.3,
    right: -width * 0.3,
    backgroundColor: 'rgba(138, 43, 226, 0.08)',
  },
  circle2: {
    width: Math.max(width, height) * 1.1,
    height: Math.max(width, height) * 1.1,
    bottom: -height * 0.2,
    left: -width * 0.2,
    backgroundColor: 'rgba(138, 43, 226, 0.05)',
  },
  circle3: {
    width: Math.min(width, height) * 0.5,
    height: Math.min(width, height) * 0.5,
    top: '30%',
    right: '10%',
    backgroundColor: 'rgba(138, 43, 226, 0.1)',
  },
  content: {
    alignItems: 'center',
    padding: 20,
  },
  subtitle: {
    color: '#8a2be2',
    fontSize: 16,
    letterSpacing: 4,
    marginBottom: 10,
    fontFamily: 'sans-serif-medium',
  },
  titleContainer: {
    overflow: 'hidden',
  },
  title: {
    fontSize: Math.min(width * 0.15,130), // Responsive font size
    fontWeight: 'bold',
    color: '#9d4edd',
    marginBottom: 10,
    marginTop: Math.min(height * 0.2),
    textShadowColor: 'rgba(0, 0, 0, 0.5)',
    textShadowOffset: { width: 2, height: 2 },
    textShadowRadius: 10,
    letterSpacing: 2,
    textAlign: 'center',
    paddingHorizontal: 20,
  },
  // Gradient text is applied through the LinearGradient component
  taglineContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    alignItems: 'center',
    padding: 20,
    paddingBottom: 30,
  },
  tagline: {
    fontSize: Math.min(width * 0.035, 15),
    color: '#b19cd9',
    textAlign: 'center',
    letterSpacing: 1,
    lineHeight: Math.min(width * 0.06, 24),
    maxWidth: '90%',
    minWidth: 250,
    opacity: 0.9,
    marginBottom: 0,
    paddingHorizontal: 20,
    includeFontPadding: false,
  },
  divider: {
    width: 100,
    height: 4,
    backgroundColor: '#8a2be2',
    marginVertical: Math.min(height * 0.05, 30),
    borderRadius: 2,
  },
  comingSoon: {
    color: '#939393ff',
    fontWeight: 'bold',
    fontSize: Math.min(width * 0.055, 25),
    textTransform: 'uppercase',
    letterSpacing: Math.min(width * 0.01, 4),
    marginTop: Math.min(height * 0.03, 20),
    marginBottom: Math.min(height * 0.02, 10),
    textAlign: 'center',
    paddingHorizontal: 20, // Add horizontal padding on small screens
  },
  version: {
    color: '#4d4d4dff',
    fontSize: Math.min(width * 0.03, 12),
    letterSpacing: Math.min(width * 0.005, 2),
    marginTop: Math.min(height * 0.2),
    textAlign: 'center',
    paddingHorizontal: 20, // Add horizontal padding on small screens
  },
  socialIcons: {
    flexDirection: 'row',
    marginTop: 60,
  },
  socialIcon: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 24,
    marginHorizontal: 15,
  },
});
